import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, ButtonProps, Link, Typography, TypographyProps } from '@mui/material';
import { EnhancedDialog } from 'components/enhanced/dialog/EnhancedDialog';
import { toast } from 'shared/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StyledDropzone from './StyledDropzone';

const importButtonProps: ButtonProps = {
  variant: 'text',
  color: 'success',
  size: 'medium',
  startIcon: <FileUploadIcon />,
  className: 'normal-case font-normal',
  sx: {
    '& .MuiButton-startIcon': {
      color: '#006DB0'
    }
  }
};

const buttonTypography: TypographyProps = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 500,
  fontSize: '13px',
  lineHeight: '1.2', // 120%
  letterSpacing: '0em',
  textTransform: 'none',
  color: '#006DB0'
};

interface ImportFileUploaderProps {
  service: any;
  onUploadSuccess?: any;
  entityId?: number;
  templateGetterMethod?: any;
  importMethod?: any;
  open?: boolean;
  onClose?: () => void;
  hideTriggerButton?: boolean;
  title?: string;
  subtitle?: string;
  infoBox?: boolean;
}

function ImportFileUploader({
  service,
  onUploadSuccess,
  entityId,
  templateGetterMethod,
  importMethod,
  open,
  onClose,
  hideTriggerButton,
  title,
  subtitle,
  infoBox = true
}: ImportFileUploaderProps) {
  const { t } = useTranslation();

  const [openDropzone, setOpenDropzone] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const isControlled = typeof open === 'boolean';
  const isDialogOpen: boolean = isControlled ? open : openDropzone;

  const attachFile = () => {
    if (!isControlled) {
      setOpenDropzone(true);
    }
  };

  const closeDialog = () => {
    setFileToUpload(null);
    if (isControlled) {
      onClose?.();
      return;
    }
    setOpenDropzone(false);
  };

  const handleUpload = () => {
    if (fileToUpload) {
      if (service.importFromFileDetail && entityId) {
        service.importFromFileDetail(fileToUpload, entityId).then(() => {
          if (onUploadSuccess) onUploadSuccess();
          toast.success(t('global.import-started'));
          closeDialog();
        });
      } else if (importMethod) {
        importMethod(fileToUpload).then(() => {
          if (onUploadSuccess) onUploadSuccess();
          toast.success(t('global.import-started'));
          closeDialog();
        });
      } else if (service.importFromFile) {
        service.importFromFile(fileToUpload).then(() => {
          if (onUploadSuccess) onUploadSuccess();
          toast.success(t('global.import-started'));
          closeDialog();
        });
      }
    }
  };

  const handleClose = () => {
    setFileToUpload(null);
    if (isControlled) {
      onClose?.();
      return;
    }
    setOpenDropzone(false);
  };

  const handleDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      toast.error(t('global.import-size-error'));
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      setFileToUpload(file);
    }
  };

  return (
    <>
      {!hideTriggerButton && (
        <Button {...importButtonProps} onClick={attachFile}>
          <Typography {...buttonTypography}>{t('data-grid.import')}</Typography>
        </Button>
      )}

      <EnhancedDialog
        open={isDialogOpen}
        onClose={handleClose}
        onSubmit={handleUpload}
        title={title || t(`import-modal.title`)}
        subTitle={subtitle}
        submitLabel={t('actions.upload')}
        isValid={Boolean(fileToUpload)}
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="sm">
        {/* Box Informativo e Download Template */}
        {infoBox && (
          <div className="flex items-start gap-3 p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
            <InfoOutlinedIcon className="text-blue-500 mt-0.5" fontSize="small" />
            <div className="flex flex-col gap-1 flex-1">
              <span className="text-xs font-normal text-slate-600 leading-normal">
                {t(
                  'import-modal.description',
                  'Per assicurare una corretta importazione dei dati, scarica il modello Excel preconfigurato compilando i campi richiesti.'
                )}
              </span>
              <Link
                underline="none"
                onClick={() =>
                  templateGetterMethod ? templateGetterMethod() : service.getImportTemplate()
                }
                className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors mt-1 w-fit">
                <DownloadIcon sx={{ fontSize: 14 }} />
                {t('actions.download-template')}
              </Link>
            </div>
          </div>
        )}

        {/* Contenitore Dropzone*/}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden mt-2">
          <StyledDropzone onDrop={handleDrop} />
        </div>
      </EnhancedDialog>
    </>
  );
}

export default ImportFileUploader;
